import { useState } from 'react';
import { TrendingUp } from 'lucide-react';
import CodeCompositionChart from '../charts/CodeCompositionChart';
import { useCodeCompositionQuery, type MetricsCardFetchScope } from './metricsCardQueries';

type CodeCompositionOverTimeCardProps = {
  fetchScope?: MetricsCardFetchScope;
  className?: string;
};

/**
 * Stacked bar trend of new vs existing vs rework composition by week or month.
 */
export default function CodeCompositionOverTimeCard({
  fetchScope,
  className = '',
}: CodeCompositionOverTimeCardProps) {
  const [windowDays, setWindowDays] = useState(30);
  const [chartPeriod, setChartPeriod] = useState<'weekly' | 'monthly'>('weekly');

  const byPeriodQuery = useCodeCompositionQuery(fetchScope, windowDays, { period: chartPeriod });
  const compositionByPeriod = byPeriodQuery.data?.compositionByPeriod ?? null;
  const loading = byPeriodQuery.isPending;

  const windowSelect = fetchScope?.orgId ? (
    <>
      <label htmlFor="code-composition-over-time-window-select" className="sr-only">
        Composition over time window (days)
      </label>
      <select
        id="code-composition-over-time-window-select"
        value={String(windowDays)}
        onChange={(e) => setWindowDays(Number(e.target.value))}
        className="select-mdl py-2 min-w-[140px] text-sm"
        aria-label="Composition over time window (days)"
      >
        <option value="7">7 days</option>
        <option value="30">30 days</option>
        <option value="365">365 days</option>
      </select>
    </>
  ) : null;

  const cardHeader = (
    <div className="flex flex-nowrap items-center gap-3 px-6 pt-6 pb-3 w-full">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <TrendingUp className="w-5 h-5 shrink-0 text-vivid-red" aria-hidden />
        <h3 className="truncate text-lg font-medium text-slate-100">Composition over time</h3>
      </div>
      {windowSelect ? <div className="ml-auto shrink-0">{windowSelect}</div> : null}
    </div>
  );

  const rootClass = ['mt-10', className].filter(Boolean).join(' ');

  return (
    <div className={rootClass}>
      <div className="card-mdl-static overflow-hidden">
        {cardHeader}
        <div className="px-6 pb-6">
          <CodeCompositionChart
            compositionByPeriod={compositionByPeriod}
            period={chartPeriod}
            onPeriodChange={setChartPeriod}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
}
